import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getServicePath } from '../data/staticServicesCatalog';
import { SITE_BRAND_NAME } from '../constants/siteBranding';
import { buildServiceCardImgSrc } from '../utils/resolveMediaUrl';
import './ServicesScrollSection.css';

function slugFromTitle(title) {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const AUTO_SCROLL_SPEED = 2;
const ARROW_SCROLL_PX = 1040;

function runAutoScroll(viewportRef, autoScrollRef) {
  return setInterval(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.scrollLeft += AUTO_SCROLL_SPEED;
    const half = el.scrollWidth / 2;
    if (el.scrollLeft >= half) el.scrollLeft -= half;
  }, 20);
}

function ServiceCardImage({ imageUrl, title }) {
  const initial = useMemo(() => buildServiceCardImgSrc(imageUrl), [imageUrl]);

  const onError = useCallback((e) => {
    e.currentTarget.onerror = null;
    const { src } = e.currentTarget;
    if (!src.includes('KHP_LOGO')) {
      e.currentTarget.src = buildServiceCardImgSrc(null);
    }
  }, []);

  return (
    <img
      src={initial}
      alt={`${title} — ${SITE_BRAND_NAME}, Ahmedabad`}
      loading="lazy"
      decoding="async"
      onError={onError}
      title={title}
    />
  );
}

function ServicesScrollSection({ services }) {
  const viewportRef = useRef(null);
  const autoScrollRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || pausedRef.current) return;
    autoScrollRef.current = runAutoScroll(viewportRef, autoScrollRef);
    return () => clearInterval(autoScrollRef.current);
  }, [services]);

  const scroll = (direction) => {
    const el = viewportRef.current;
    if (!el) return;
    clearInterval(autoScrollRef.current);
    el.scrollBy({ left: direction * ARROW_SCROLL_PX, behavior: 'smooth' });
    setTimeout(() => {
      if (!pausedRef.current) autoScrollRef.current = runAutoScroll(viewportRef, autoScrollRef);
    }, 400);
  };

  const pause = () => {
    pausedRef.current = true;
    clearInterval(autoScrollRef.current);
  };
  const resume = () => {
    pausedRef.current = false;
    if (viewportRef.current) autoScrollRef.current = runAutoScroll(viewportRef, autoScrollRef);
  };

  if (!services || services.length === 0) return null;

  const list = services.map((s) => ({
    id: s.id || s._id,
    title: s.title,
    category: s.category,
    description: s.description || '',
    image_url: s.image_url,
    slug: s.slug || slugFromTitle(s.title)
  }));

  const hrefFor = (service) => getServicePath(service.slug || slugFromTitle(service.title));

  const duplicated = [...list, ...list];

  return (
    <section className="services-scroll-section animate-on-scroll" id="services">
      <div className="services-scroll-container">
        <h2 className="services-scroll-title">Our Services</h2>
        <p className="services-scroll-sub">Professional solutions for offices, residential, and industrial spaces</p>
        <div className="services-scroll-viewport-wrapper">
          <button
            type="button"
            className="services-scroll-arrow services-scroll-arrow--left"
            onClick={() => scroll(-1)}
            aria-label="Previous services"
          >
            ‹
          </button>
          <div
            className="services-scroll-viewport"
            ref={viewportRef}
            onMouseEnter={pause}
            onMouseLeave={resume}
          >
            <div className="services-scroll-track" aria-hidden="true">
              {duplicated.map((service, index) => (
                <Link
                  key={`${service.id || service._id}-${index}`}
                  to={hrefFor(service)}
                  className="services-scroll-card"
                >
                  <div className="services-scroll-card-image">
                    <ServiceCardImage imageUrl={service.image_url} title={service.title} />
                    <div className="services-scroll-card-accent" />
                  </div>
                  <div className="services-scroll-card-body">
                    <span className="services-scroll-card-category">{service.category}</span>
                    <h3 className="services-scroll-card-title">{service.title}</h3>
                    {service.description && (
                      <p className="services-scroll-card-desc">{service.description}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="services-scroll-arrow services-scroll-arrow--right"
            onClick={() => scroll(1)}
            aria-label="Next services"
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );
}

export default ServicesScrollSection;
